import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useDarkMode } from "../../context/DarkModeContext";

interface WhitelistRequest {
  id: string;
  userId: string;
  email: string;
  fullName: string;
  company?: string;
  position: string;
  expectedVolume: string;
  reason: string;
  requestType: "job_posting" | "premium_access";
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  reviewedBy?: string;
  reviewedAt?: string;
  adminNotes?: string;
}

interface MyRequestsResponse {
  requests: WhitelistRequest[];
}

const API_BASE = import.meta.env.VITE_API_BASE || "";

const UserWhitelistRequest: React.FC = () => {
  const { user } = useAuth();
  const { isDark } = useDarkMode();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeForm, setActiveForm] = useState<
    "job_posting" | "premium_access"
  >("job_posting");
  const [myRequests, setMyRequests] = useState<WhitelistRequest[]>([]);

  // Form data for job posting request
  const [jobPostingForm, setJobPostingForm] = useState({
    company: "",
    position: "",
    expectedVolume: "",
    reason: "",
  });

  // Form data for premium access request
  const [premiumAccessForm, setPremiumAccessForm] = useState({
    reason: "",
    desiredFeatures: [] as string[],
  });

  // Fetch user's existing whitelist requests
  useEffect(() => {
    if (user) {
      fetchMyRequests();
    }
  }, [user]);

  const fetchMyRequests = async () => {
    if (!user) return;

    try {
      const response = await fetch(`${API_BASE}/whitelist/my-requests`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.ok) {
        const data: MyRequestsResponse = await response.json();
        setMyRequests(data.requests || []);

        // Check if user has a pending request
        const hasPendingRequest = data.requests?.some(
          (request: WhitelistRequest) => request.status === "pending",
        );
        if (hasPendingRequest) {
          setSubmitted(true);
        }
      }
    } catch (error) {
      console.error("Error fetching whitelist requests:", error);
    }
  };

  const handleJobPostingSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/whitelist/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...jobPostingForm,
          requestType: "job_posting",
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setJobPostingForm({
          company: "",
          position: "",
          expectedVolume: "",
          reason: "",
        });
        fetchMyRequests();
      } else {
        console.error("Failed to submit whitelist request");
        alert("Failed to submit request. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting whitelist request:", error);
      alert("Error submitting request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePremiumAccessSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/whitelist/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          ...premiumAccessForm,
          requestType: "premium_access",
          company: "N/A", // Not applicable for premium access
          position: "N/A",
          expectedVolume: "N/A",
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setPremiumAccessForm({
          reason: "",
          desiredFeatures: [],
        });
        fetchMyRequests();
      } else {
        console.error("Failed to submit whitelist request");
        alert("Failed to submit request. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting whitelist request:", error);
      alert("Error submitting request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJobPostingInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setJobPostingForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePremiumAccessInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setPremiumAccessForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFeatureToggle = (feature: string) => {
    setPremiumAccessForm((prev) => ({
      ...prev,
      desiredFeatures: prev.desiredFeatures.includes(feature)
        ? prev.desiredFeatures.filter((f) => f !== feature)
        : [...prev.desiredFeatures, feature],
    }));
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "px-3 py-1 rounded-full text-xs font-medium";

    switch (status) {
      case "approved":
        return `${baseClasses} ${
          isDark
            ? "bg-green-900/30 text-green-300"
            : "bg-green-100 text-green-800"
        }`;
      case "rejected":
        return `${baseClasses} ${
          isDark ? "bg-red-900/30 text-red-300" : "bg-red-100 text-red-800"
        }`;
      case "pending":
      default:
        return `${baseClasses} ${
          isDark
            ? "bg-yellow-900/30 text-yellow-300"
            : "bg-yellow-100 text-yellow-800"
        }`;
    }
  };

  const getRequestTypeBadge = (type: string) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";

    switch (type) {
      case "job_posting":
        return `${baseClasses} ${
          isDark ? "bg-blue-900/30 text-blue-300" : "bg-blue-100 text-blue-800"
        }`;
      case "premium_access":
        return `${baseClasses} ${
          isDark
            ? "bg-purple-900/30 text-purple-300"
            : "bg-purple-100 text-purple-800"
        }`;
      default:
        return `${baseClasses} ${
          isDark ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-700"
        }`;
    }
  };

  if (!user) {
    return (
      <div
        className={`min-h-screen py-8 ${isDark ? "bg-gray-900 text-white" : "bg-blue-50 text-gray-900"}`}
      >
        <div className="max-w-4xl mx-auto px-4">
          <div
            className={`rounded-lg p-6 text-center ${isDark ? "bg-gray-800" : "bg-white"}`}
          >
            <p>Please log in to request whitelist access.</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    const pendingRequest = myRequests.find((req) => req.status === "pending");

    return (
      <div
        className={`min-h-screen py-8 ${isDark ? "bg-gray-900 text-white" : "bg-blue-50 text-gray-900"}`}
      >
        <div className="max-w-4xl mx-auto px-4">
          <div
            className={`rounded-lg p-8 text-center ${isDark ? "bg-gray-800" : "bg-white"} shadow-lg`}
          >
            {pendingRequest ? (
              <>
                <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4">
                  Request Pending Review
                </h2>
                <p className="mb-4">
                  Your{" "}
                  {pendingRequest.requestType === "job_posting"
                    ? "job posting"
                    : "premium access"}{" "}
                  request has been submitted and is currently under review. Our
                  team will get back to you soon.
                </p>
                <div
                  className={`p-4 rounded-lg mb-6 text-left ${isDark ? "bg-gray-700" : "bg-gray-50"}`}
                >
                  <h3 className="font-semibold mb-2">Your Request Details:</h3>
                  <div className="flex gap-2 mb-2">
                    <span
                      className={getRequestTypeBadge(
                        pendingRequest.requestType,
                      )}
                    >
                      {pendingRequest.requestType === "job_posting"
                        ? "Job Posting"
                        : "Premium Access"}
                    </span>
                    <span className={getStatusBadge(pendingRequest.status)}>
                      {pendingRequest.status}
                    </span>
                  </div>
                  {pendingRequest.requestType === "job_posting" && (
                    <>
                      <p>
                        <strong>Company:</strong>{" "}
                        {pendingRequest.company || "Not provided"}
                      </p>
                      <p>
                        <strong>Position:</strong> {pendingRequest.position}
                      </p>
                      <p>
                        <strong>Expected Volume:</strong>{" "}
                        {pendingRequest.expectedVolume}
                      </p>
                    </>
                  )}
                  <p>
                    <strong>Reason:</strong> {pendingRequest.reason}
                  </p>
                  <p>
                    <strong>Submitted:</strong>{" "}
                    {new Date(pendingRequest.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4">Request Submitted!</h2>
                <p className="mb-6">
                  Your whitelist request has been submitted successfully. Our
                  team will review your request and get back to you soon.
                </p>
              </>
            )}

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => setSubmitted(false)}
                className={`px-6 py-2 rounded-lg font-semibold ${
                  isDark
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                } transition-colors`}
              >
                {pendingRequest ? "View Status" : "Submit Another Request"}
              </button>
              <button
                onClick={fetchMyRequests}
                className={`px-6 py-2 rounded-lg font-semibold border ${
                  isDark
                    ? "border-gray-600 hover:bg-gray-700 text-white"
                    : "border-gray-300 hover:bg-gray-100 text-gray-700"
                } transition-colors`}
              >
                Refresh Status
              </button>
            </div>
          </div>

          {/* Previous Requests Section */}
          {myRequests.length > 0 && (
            <div
              className={`mt-8 rounded-lg p-6 ${isDark ? "bg-gray-800" : "bg-white"} shadow-lg`}
            >
              <h3 className="text-xl font-bold mb-4">Your Previous Requests</h3>
              <div className="space-y-4">
                {myRequests
                  .filter((req) => req.status !== "pending")
                  .map((request) => (
                    <div
                      key={request.id}
                      className={`p-4 rounded-lg border ${
                        isDark
                          ? "border-gray-700 bg-gray-750"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex gap-2 mb-2">
                            <span
                              className={getRequestTypeBadge(
                                request.requestType,
                              )}
                            >
                              {request.requestType === "job_posting"
                                ? "Job Posting"
                                : "Premium Access"}
                            </span>
                            <span className={getStatusBadge(request.status)}>
                              {request.status.charAt(0).toUpperCase() +
                                request.status.slice(1)}
                            </span>
                          </div>
                          <p className="font-semibold">
                            Submitted on{" "}
                            {new Date(request.createdAt).toLocaleDateString()}
                          </p>
                          {request.requestType === "job_posting" && (
                            <>
                              {request.company && request.company !== "N/A" && (
                                <p className="text-sm opacity-75">
                                  Company: {request.company}
                                </p>
                              )}
                              <p className="text-sm opacity-75">
                                Position: {request.position}
                              </p>
                              <p className="text-sm opacity-75">
                                Expected Volume: {request.expectedVolume}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      <p className="mb-2">
                        <strong>Reason:</strong> {request.reason}
                      </p>
                      {request.adminNotes && (
                        <p
                          className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
                        >
                          <strong>Admin Notes:</strong> {request.adminNotes}
                        </p>
                      )}
                      {request.reviewedAt && (
                        <p
                          className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Reviewed on{" "}
                          {new Date(request.reviewedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 ${isDark ? "bg-gray-900 text-white" : "bg-blue-50 text-gray-900"}`}
    >
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Request Whitelist Access</h1>
          <p className={`${isDark ? "text-gray-300" : "text-gray-600"}`}>
            Choose the type of access you need and submit your request
          </p>
        </div>

        {/* Form Type Selector */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveForm("job_posting")}
            className={`flex-1 py-4 px-6 rounded-lg border-2 transition-all ${
              activeForm === "job_posting"
                ? isDark
                  ? "border-blue-500 bg-blue-900/20 text-white"
                  : "border-blue-500 bg-blue-500 text-white"
                : isDark
                  ? "border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-600"
                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
            }`}
          >
            <div className="text-lg font-semibold mb-1">Job Posting Access</div>
            <div className="text-sm opacity-75">
              For employers and recruiters
            </div>
          </button>

          <button
            onClick={() => setActiveForm("premium_access")}
            className={`flex-1 py-4 px-6 rounded-lg border-2 transition-all ${
              activeForm === "premium_access"
                ? isDark
                  ? "border-purple-500 bg-purple-900/20 text-white"
                  : "border-purple-500 bg-purple-500 text-white"
                : isDark
                  ? "border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-600"
                  : "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
            }`}
          >
            <div className="text-lg font-semibold mb-1">
              Premium Features Access
            </div>
            <div className="text-sm opacity-75">
              For job seekers and professionals
            </div>
          </button>
        </div>

        <div
          className={`rounded-lg p-6 ${isDark ? "bg-gray-800" : "bg-white"} shadow-lg`}
        >
          {/* Job Posting Form */}
          {activeForm === "job_posting" && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">
                  Job Posting Access Request
                </h2>
                <p className={`${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Request access to post jobs and manage listings on our
                  platform
                </p>
              </div>

              <div
                className={`mb-6 p-4 rounded-lg ${isDark ? "bg-gray-700" : "bg-blue-100"}`}
              >
                <h3 className="font-semibold mb-2">
                  Benefits for Job Posters:
                </h3>
                <ul
                  className={`text-sm space-y-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  <li>• Post unlimited job listings</li>
                  <li>• Access to premium candidate pool</li>
                  <li>• Advanced job management tools</li>
                  <li>• Priority customer support</li>
                  <li>• Analytics and insights dashboard</li>
                </ul>
              </div>

              <form onSubmit={handleJobPostingSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user.email}
                      disabled
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-gray-300"
                          : "bg-gray-50 border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={user.name || `${user.firstName} ${user.lastName}`}
                      disabled
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-gray-300"
                          : "bg-gray-50 border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium mb-2"
                  >
                    Company (Optional)
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={jobPostingForm.company}
                    onChange={handleJobPostingInputChange}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Enter your company name"
                  />
                </div>

                <div>
                  <label
                    htmlFor="position"
                    className="block text-sm font-medium mb-2"
                  >
                    Position / Role *
                  </label>
                  <input
                    type="text"
                    id="position"
                    name="position"
                    value={jobPostingForm.position}
                    onChange={handleJobPostingInputChange}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="e.g., HR Manager, Recruiter, Hiring Manager"
                  />
                </div>

                <div>
                  <label
                    htmlFor="expectedVolume"
                    className="block text-sm font-medium mb-2"
                  >
                    Expected Job Volume *
                  </label>
                  <select
                    id="expectedVolume"
                    name="expectedVolume"
                    value={jobPostingForm.expectedVolume}
                    onChange={handleJobPostingInputChange}
                    required
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 focus:border-blue-500"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  >
                    <option value="">Select expected volume</option>
                    <option value="1-5 jobs/month">1-5 jobs/month</option>
                    <option value="6-10 jobs/month">6-10 jobs/month</option>
                    <option value="11-20 jobs/month">11-20 jobs/month</option>
                    <option value="21+ jobs/month">21+ jobs/month</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="reason"
                    className="block text-sm font-medium mb-2"
                  >
                    Why do you need job posting access? *
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={jobPostingForm.reason}
                    onChange={handleJobPostingInputChange}
                    required
                    rows={4}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-blue-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500"
                    } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Please explain why you need job posting access and what types of roles you plan to post..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !jobPostingForm.reason.trim() ||
                    !jobPostingForm.position.trim() ||
                    !jobPostingForm.expectedVolume
                  }
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    isSubmitting ||
                    !jobPostingForm.reason.trim() ||
                    !jobPostingForm.position.trim() ||
                    !jobPostingForm.expectedVolume
                      ? isDark
                        ? "bg-gray-600 cursor-not-allowed text-gray-400"
                        : "bg-gray-400 cursor-not-allowed text-gray-200"
                      : isDark
                        ? "bg-blue-600 hover:bg-blue-700 text-white"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : "Submit Job Posting Request"}
                </button>
              </form>
            </>
          )}

          {/* Premium Access Form */}
          {activeForm === "premium_access" && (
            <>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2">
                  Premium Features Access Request
                </h2>
                <p className={`${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Request access to premium features for job seekers and
                  professionals
                </p>
              </div>

              <div
                className={`mb-6 p-4 rounded-lg ${isDark ? "bg-gray-700" : "bg-purple-100"}`}
              >
                <h3 className="font-semibold mb-2">Premium Features:</h3>
                <ul
                  className={`text-sm space-y-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                >
                  <li>• Advanced job search filters</li>
                  <li>• Priority application processing</li>
                  <li>• Enhanced profile visibility</li>
                  <li>• Salary insights and analytics</li>
                  <li>• Direct messaging with recruiters</li>
                  <li>• Exclusive networking events</li>
                </ul>
              </div>

              <form onSubmit={handlePremiumAccessSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user.email}
                      disabled
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-gray-300"
                          : "bg-gray-50 border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={user.name || `${user.firstName} ${user.lastName}`}
                      disabled
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-gray-300"
                          : "bg-gray-50 border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Desired Premium Features (Optional)
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      "Advanced Job Search",
                      "Priority Applications",
                      "Profile Boosting",
                      "Salary Insights",
                      "Direct Messaging",
                      "Networking Events",
                      "Career Analytics",
                      "Resume Review",
                    ].map((feature) => (
                      <label
                        key={feature}
                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                          premiumAccessForm.desiredFeatures.includes(feature)
                            ? isDark
                              ? "bg-purple-900/30 border-purple-500"
                              : "bg-purple-100 border-purple-500"
                            : isDark
                              ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                              : "bg-white border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={premiumAccessForm.desiredFeatures.includes(
                            feature,
                          )}
                          onChange={() => handleFeatureToggle(feature)}
                          className="mr-3"
                        />
                        <span>{feature}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="premiumReason"
                    className="block text-sm font-medium mb-2"
                  >
                    Why do you want premium access? *
                  </label>
                  <textarea
                    id="premiumReason"
                    name="reason"
                    value={premiumAccessForm.reason}
                    onChange={handlePremiumAccessInputChange}
                    required
                    rows={4}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:border-purple-500"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-purple-500"
                    } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                    placeholder="Please explain why you need premium access and how it will help your job search or career development..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !premiumAccessForm.reason.trim()}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                    isSubmitting || !premiumAccessForm.reason.trim()
                      ? isDark
                        ? "bg-gray-600 cursor-not-allowed text-gray-400"
                        : "bg-gray-400 cursor-not-allowed text-gray-200"
                      : isDark
                        ? "bg-purple-600 hover:bg-purple-700 text-white"
                        : "bg-purple-500 hover:bg-purple-600 text-white"
                  }`}
                >
                  {isSubmitting
                    ? "Submitting..."
                    : "Submit Premium Access Request"}
                </button>
              </form>
            </>
          )}

          {/* Show existing requests if any */}
          {myRequests.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-700">
              <h3 className="text-lg font-semibold mb-4">
                Your Previous Requests
              </h3>
              <div className="space-y-3">
                {myRequests.map((request) => (
                  <div
                    key={request.id}
                    className={`p-3 rounded-lg ${isDark ? "bg-gray-700" : "bg-gray-100"}`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex gap-2">
                        <span
                          className={getRequestTypeBadge(request.requestType)}
                        >
                          {request.requestType === "job_posting"
                            ? "Job Posting"
                            : "Premium Access"}
                        </span>
                        <span className={getStatusBadge(request.status)}>
                          {request.status}
                        </span>
                      </div>
                      <span className="text-sm">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    {request.requestType === "job_posting" && (
                      <p className="text-xs mb-1">
                        Position: {request.position} | Volume:{" "}
                        {request.expectedVolume}
                      </p>
                    )}
                    <p className="text-xs opacity-75 truncate">
                      {request.reason}
                    </p>
                    {request.adminNotes && (
                      <p className="text-xs mt-1 opacity-75">
                        Note: {request.adminNotes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserWhitelistRequest;
